import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, ChevronRight, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { breadcrumbSchema, siteUrl } from "../lib/schema";
import Seo from "../components/Seo";
import { PostCard } from "../components/ContentBlocks";
import ShareMenu from "../components/ShareMenu";
import FollowStrip from "../components/FollowStrip";
import DSPButtons from "../components/DSPButtons";
import EmailCapture from "../components/EmailCapture";
import RelatedReleases from "../components/RelatedReleases";
import Footer from "../components/Footer";
import NotFound from "./NotFound";

export function News() {
  const settings = useSettings();
  const [posts, setPosts] = useState([]);
  useEffect(() => { window.scrollTo(0, 0); track("news_view", {}); api.get("/posts?limit=50").then((r) => setPosts(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo title="News & Stories" description="New releases, music videos, behind-the-song stories, collaborations and milestones from DJ Código — the Dominican Latin urban artist." path="/news" jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "News", path: "/news" }], settings)} />
      <div className="px-6 pt-28 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold">Behind the Código</p>
          <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-6xl">News & Stories</h1>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
            {posts.map((p, i) => <PostCard key={p.internalId} post={p} index={i} />)}
          </div>
          {posts.length === 0 && <p className="mt-8 text-sm text-white/40">Stories coming soon. Join the Código List to hear it first.</p>}
        </div>
      </div>
      <EmailCapture source="news" />
      <Footer settings={settings} />
    </div>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const settings = useSettings();
  const { playRelease } = usePlayer();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/posts/${slug}`).then((r) => { setPost(r.data); setStatus("ok"); track("news_post_view", { releaseId: r.data.releaseId, metadata: { slug } }); }).catch(() => setStatus("404"));
  }, [slug]);
  if (status === "404") return <NotFound />;
  if (!post) return <div className="min-h-screen bg-ink" />;
  const rel = post.release;
  const article = {
    "@context": "https://schema.org", "@type": "NewsArticle", headline: post.title, description: post.excerpt, image: post.coverImage,
    datePublished: post.publishedAt, dateModified: post.updatedAt, url: `${siteUrl(settings)}/news/${post.slug}`,
    author: { "@type": "MusicGroup", name: "DJ Código", url: siteUrl(settings) }, publisher: { "@type": "Organization", name: "DJ Código", url: siteUrl(settings) },
    ...(rel ? { about: { "@type": "MusicRecording", name: rel.title, url: `${siteUrl(settings)}/music/${rel.slug}` } } : {}),
  };
  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo title={post.title} description={post.excerpt || post.body?.slice(0, 160)} image={post.coverImage} type="article" path={`/news/${post.slug}`} jsonLd={[article, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "News", path: "/news" }, { name: post.title, path: `/news/${post.slug}` }], settings)]} />
      <article className="px-6 pt-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-3xl">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/50">
            <Link to="/" className="inline-flex items-center gap-1.5 hover:text-gold"><ArrowLeft size={14} /> Home</Link><ChevronRight size={12} /><Link to="/news" className="hover:text-gold">News</Link>
          </nav>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gold">{post.category} • {post.publishedAt?.slice(0, 10)}</p>
          <h1 data-testid="post-title" className="mt-3 font-display text-3xl font-bold uppercase leading-tight tracking-tight text-white sm:text-5xl">{post.title}</h1>
          {post.excerpt && <p className="mt-4 text-lg leading-relaxed text-white/70">{post.excerpt}</p>}
          {post.coverImage && <img src={post.coverImage} alt={post.title} className="mt-8 w-full rounded-3xl border border-white/10 object-cover" />}
          <div data-testid="post-body" className="mt-8 space-y-5 text-base leading-relaxed text-white/75">
            {(post.body || "").split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          {rel && (
            <div data-testid="post-release" className="mt-10 flex flex-wrap items-center gap-4 rounded-3xl border border-gold/30 bg-surface/60 p-5">
              <img src={rel.artwork} alt={`${rel.title} cover`} className="h-20 w-20 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gold">Stream the song</p>
                <Link to={`/music/${rel.slug}`} className="font-display text-xl font-semibold text-white hover:text-gold">{rel.title}</Link>
                <div className="mt-2"><DSPButtons release={rel} size="sm" /></div>
              </div>
              <button onClick={() => playRelease(rel)} className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-black"><Play size={16} fill="currentColor" /> Listen</button>
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ShareMenu kind="n" slug={post.slug} title={post.title} text={post.title} eventType="share_song" label="Share story" />
          </div>
          <FollowStrip settings={settings} placement="news" className="mt-8" />
        </div>
      </article>
      <RelatedReleases current={rel} overline="More from DJ Código" title="Keep Listening" />
      <EmailCapture source={`news:${post.slug}`} releaseId={post.releaseId} />
      <Footer settings={settings} />
    </div>
  );
}
